import React, { useState } from 'react';

import './CalendarPopup.scss';
import { getString } from '../../helpers/styles';

import LeftArrow from '../../shared/assets/icons/leftArrow.svg';
import RightArrow from '../../shared/assets/icons/rightArrow.svg';

const daysLabels = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const monthLabels = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December'
];

export default function CalendarPopup(props) {
  const {
    year,
    month,
    field,
    markedDays,
    togglePopup,
    withTriangle,
    disableSwitching,
    changeSelectedDate,
    isLimitedByPregnancyPeriod,
    isLimitedByPastPregnancyPeriod
  } = props;

  const limitedPeriod = isLimitedByPregnancyPeriod
    ? getPregnancyPeriod()
    : isLimitedByPastPregnancyPeriod
      ? getPastPregnancyPeriod()
      : {};

  const [period, changePeriod] = useState({ month, year });

  const logicalMonth = period.month - 1;

  const firstDay = new Date(period.year, logicalMonth, 1);
  const firstDayWeekday = firstDay.getDay() === 0 ? 7 : firstDay.getDay();

  const monthLength = new Date(period.year, period.month, 0).getDate();
  const previousMonthLength = new Date(period.year, logicalMonth, 0).getDate();

  const isStartOfLimitedPeriod =
    limitedPeriod.start &&
    period.year <= limitedPeriod.start.getFullYear() &&
    period.month <= limitedPeriod.start.getMonth() + 1;

  const isEndOfLimitedPeriod =
    limitedPeriod.end &&
    period.year >= limitedPeriod.end.getFullYear() &&
    period.month >= limitedPeriod.end.getMonth() + 1;

  return (
    <section
      id="calendarPopup"
      className={getString([
        {
          value: 'calendarPopup_switching_disabled',
          condition: disableSwitching
        },
        {
          value: 'calendarPopup_limited_period',
          condition:
            isLimitedByPregnancyPeriod || isLimitedByPastPregnancyPeriod
        }
      ])}
    >
      {withTriangle && (
        <div id="calendarPopup_triangle">
          <span />
        </div>
      )}

      <div id="calendarPopup_header">
        <span
          onClick={() => changeMonth(period.month - 1)}
          className={getString(
            'calendarPopup_arrow_hidden',
            isStartOfLimitedPeriod
          )}
        >
          <LeftArrow />
        </span>

        <div>
          {monthLabels[logicalMonth]}

          <span
            onClick={() =>
              changePeriod({ month: period.month, year: period.year - 1 })
            }
            className="calendarPopup_year_arrow"
          >
            <LeftArrow />
          </span>

          <span className="calendarPopup_year">{period.year}</span>

          <span
            onClick={() =>
              changePeriod({ month: period.month, year: period.year + 1 })
            }
            className="calendarPopup_year_arrow"
          >
            <RightArrow />
          </span>
        </div>

        <span
          onClick={() => changeMonth(period.month + 1)}
          className={getString(
            'calendarPopup_arrow_hidden',
            isEndOfLimitedPeriod
          )}
        >
          <RightArrow />
        </span>
      </div>

      <div id="calendarPopup_grid">
        <table>
          <thead>
            <tr className="calendarPopup_weekdays">
              {daysLabels.map((label, idx) => (
                <th key={idx} className="calendarPopup_day">
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            onClick={(e) => handleClick(e)}
            dangerouslySetInnerHTML={{ __html: calendarGrid() }}
          ></tbody>
        </table>
      </div>
    </section>
  );

  function calendarGrid() {
    let day = 1;
    let prev = 1;
    let next = 1;

    let markup = '<tr>';

    for (let i = 0; i < 9; i++) {
      for (let j = 1; j <= 7; j++) {
        const disabled =
          (isStartOfLimitedPeriod && day < limitedPeriod.start.getDate()) ||
          (isEndOfLimitedPeriod && day > limitedPeriod.end.getDate());

        if (day <= monthLength && (i > 0 || j >= firstDayWeekday))
          markup += `
            <td
              data-type="current"
              class="calendarPopup_day"
              data-disabled="${disabled}">
              <span class="${setClass(day, month, year)}">${day++}</span>
            </td>`;
        else if (day <= monthLength)
          markup += `
            <td
              data-type="previous"
              data-disabled="${disabled}"
              class="calendarPopup_day calendarPopup_other_month">
              <span class="${setClass(
                previousMonthLength - firstDayWeekday + prev + 1,
                month - 1,
                year
              )}">
                ${previousMonthLength - firstDayWeekday + prev++ + 1}
              </span>
            </td>`;
        else
          markup += `
          <td
            data-type="next"
            data-disabled="${disabled}"
            class="calendarPopup_day calendarPopup_other_month">
            <span class="${setClass(next, month + 1, year)}">${next++}</span>
          </td>`;
      }

      if (day > monthLength) {
        markup += '</tr>';

        break;
      } else {
        markup += '</tr><tr>';
      }
    }

    return markup;
  }

  function changeMonth(month) {
    if (month > 12) return changePeriod({ month: 1, year: period.year + 1 });
    if (month < 1) return changePeriod({ month: 12, year: period.year - 1 });

    changePeriod({ month, year: period.year });
  }

  function handleClick(e) {
    const cell = e.target.closest('td');

    const { type, disabled } = cell.dataset;

    if (disabled === 'true') return;

    const day = +cell.textContent;

    switch (type) {
      case 'current':
        return setDate(day, period.month, period.year);
      case 'next':
        const nextMonth = period.month + 1;

        if (nextMonth > 12) return setDate(day, 1, period.year + 1);
        else return setDate(day, nextMonth, period.year);
      case 'previous':
        const previousMonth = period.month - 1;

        if (previousMonth < 1) return setDate(day, 12, period.year - 1);
        else return setDate(day, previousMonth, period.year);
    }
  }

  function setClass(day, month, year) {
    if (!markedDays) return '';

    const date = new Date(year, month - 1, day);

    const time = date.getTime();

    if (markedDays.ovulationDate.getTime() === time)
      return 'calendarPopup_ovulation_day';

    if (
      time >= markedDays.fruitfulPeriod.start &&
      time <= markedDays.fruitfulPeriod.end
    )
      return 'calendarPopup_fruitful_day';

    if (
      time >= markedDays.menstruationPeriod.start &&
      time <= markedDays.menstruationPeriod.end
    )
      return 'calendarPopup_menstruation_day';

    return '';
  }

  function setDate(day, month, year) {
    togglePopup(false);

    changeSelectedDate({ day, month, year });

    document.getElementById(field).value = `${
      day < 10 ? `0${day}` : day
    }-${month < 10 ? `0${month}` : month}-${year}`;
  }
}

function getPregnancyPeriod() {
  const start = new Date();
  const end = new Date();

  // 40 weeks from current date
  end.setDate(end.getDate() + 40 * 7);

  return { start, end };
}

function getPastPregnancyPeriod() {
  const start = new Date();
  const end = new Date();

  // 40 weeks ago from current date
  start.setDate(end.getDate() - 40 * 7);

  return { start, end };
}
